import { useEffect, useState } from "react";
import axios from "axios";
import { Seo } from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PodcastPage = () => {
  const [episodes, setEpisodes] = useState(null);

  useEffect(() => {
    axios.get(`${API}/podcast`).then((res) => setEpisodes(res.data)).catch(() => setEpisodes([]));
  }, []);

  return (
    <>
      <Seo
        title="Podcast — #whatshesaidproject"
        description="The #whatshesaidproject podcast: conversations with writers, artists, speakers, and cultural leaders on coaching, life, creativity, entrepreneurship, and story."
        path="/podcast"
      />
      <section className="bg-white text-[#121212] pt-12 pb-24 md:py-28 border-b border-[#e5e5e5]">
        <div className="max-w-6xl mx-auto px-6">
          <span className="text-xs font-mono uppercase tracking-[.18em] text-[#e02020] block mb-3">
            Podcast
          </span>
          <h1
            data-testid="podcast-headline"
            className="font-disp text-[clamp(36px,5vw,72px)] leading-[1.02] tracking-tight font-normal text-[#121212] text-balance max-w-4xl mb-6"
          >
            The #whatshesaidproject podcast.
          </h1>
          <p className="text-[17px] leading-[1.65] text-[#555555] max-w-2xl mb-6">
            Conversations with writers, artists, speakers, and cultural leaders on coaching, life,
            creativity, entrepreneurship, and story. Hosted by Shannon Ivey.
          </p>
          <a
            href="https://podcasts.apple.com/us/podcast/whatshesaidproject/id1523686350"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="podcast-apple-link"
            className="text-xs font-mono uppercase tracking-[.14em] text-[#121212] underline underline-offset-4 hover:text-[#e02020] transition-colors"
          >
            Listen on Apple Podcasts →
          </a>
        </div>
      </section>

      <section className="bg-white text-[#121212] py-24 md:py-32">
        <div className="max-w-6xl mx-auto px-6">
          {episodes === null ? (
            <p className="text-xs font-mono uppercase tracking-[.14em] text-[#555555]">Loading…</p>
          ) : episodes.length === 0 ? (
            <div className="border border-dashed border-[#cccccc] p-16 text-center" data-testid="podcast-empty-state">
              <p className="font-disp text-2xl text-[#121212] mb-3">Episode archive coming soon.</p>
              <p className="text-sm text-[#555555]">
                Episodes, guests, and listening links will be published here ahead of launch.
              </p>
            </div>
          ) : (
            <div className="space-y-8">
              {episodes.map((ep) => (
                <article
                  key={ep.id}
                  data-testid={`episode-card-${ep.slug || ep.id}`}
                  className="border border-[#e5e5e5] hover:border-[#121212] transition-colors bg-white p-8 grid grid-cols-1 md:grid-cols-12 gap-8"
                >
                  {ep.artwork && (
                    <figure className="md:col-span-3 relative aspect-square bg-[#f4f4f4] border border-[#e5e5e5] overflow-hidden">
                      <img
                        src={ep.artwork}
                        alt={ep.title}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                    </figure>
                  )}
                  <div className={ep.artwork ? "md:col-span-9" : "md:col-span-12"}>
                    <div className="flex items-center gap-3 text-xs font-mono text-[#6b6b6b] mb-3">
                      {ep.episode_number && <span>Episode {ep.episode_number}</span>}
                      {ep.published_at && (
                        <>
                          <span>&bull;</span>
                          <span>
                            {new Date(ep.published_at).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })}
                          </span>
                        </>
                      )}
                    </div>
                    <h2 className="font-disp text-2xl text-[#121212] mb-3">{ep.title}</h2>
                    {ep.guest && (
                      <p className="text-xs font-mono uppercase tracking-[.14em] text-[#e02020] mb-3">
                        With {ep.guest}
                      </p>
                    )}
                    {ep.description && (
                      <p className="text-sm text-[#555555] leading-relaxed mb-6">{ep.description}</p>
                    )}
                    {ep.audio_url && (
                      <audio controls src={ep.audio_url} className="w-full mb-6" data-testid={`episode-audio-${ep.id}`} />
                    )}
                    {ep.platform_links && ep.platform_links.length > 0 && (
                      <div className="flex flex-wrap gap-4">
                        {ep.platform_links.map((link) => (
                          <a
                            key={link.name}
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`episode-link-${ep.id}-${link.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            className="text-xs font-mono uppercase tracking-[.14em] text-[#121212] underline underline-offset-4 hover:text-[#e02020]"
                          >
                            {link.name} →
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default PodcastPage;
